import * as React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { useCookies } from "react-cookie";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Logo from "../logo.png";
import SigninSection from "../components/authentication/signin/Signin";
import SignupSection from "../components/authentication/signup/Signup";

const FEATURES = [
  "Search 2M+ Instagram creators by niche, city and follower count",
  "Access verified phone numbers and emails — no cold DMs",
  "Track deliverables and automate creator payouts end-to-end",
];

const STATS = [
  { v: "2M+",   l: "Creator Profiles"    },
  { v: "3000+", l: "Campaigns Executed"  },
  { v: "95%",   l: "Contact Reach Rate"  },
];

const BRANDS = ["Flipkart","Dream11","Netflix","Paytm","Myntra","Airtel","T-Series","Sony Liv"];

function LeftPanel() {
  return (
    <Box
      sx={{
        display:       { xs: "none", lg: "flex" },
        flexDirection: "column",
        width:         "52%",
        minHeight:     "100vh",
        bgcolor:       "#0D1B3E",
        position:      "relative",
        overflow:      "hidden",
        p:             "48px 52px",
      }}
    >
      {/* Ambient glows */}
      <Box sx={{
        position: "absolute", width: 520, height: 520,
        top: "-100px", right: "-100px",
        background: "radial-gradient(circle, rgba(255,107,53,0.08) 0%, transparent 65%)",
        pointerEvents: "none",
      }} />
      <Box sx={{
        position: "absolute", width: 420, height: 420,
        bottom: "-60px", left: "-80px",
        background: "radial-gradient(circle, rgba(74,144,226,0.07) 0%, transparent 65%)",
        pointerEvents: "none",
      }} />

      {/* Content */}
      <Box sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", height: "100%" }}>

        {/* Logo */}
        <Box component="a" href="/" sx={{ display: "inline-block", mb: "52px" }}>
          <img src={Logo} alt="Meesha" width="120px" height="39px" />
        </Box>

        {/* Badge */}
        <Box component="span" sx={{
          display:       "inline-block",
          color:         "#FF6B35",
          bgcolor:       "rgba(255,107,53,0.10)",
          border:        "1px solid rgba(255,107,53,0.28)",
          px:            "12px", py: "4px",
          borderRadius:  "100px",
          fontSize:      "10px", fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.08em",
          mb:            3, width: "fit-content",
        }}>
          India's #1 Creator Intelligence Platform
        </Box>

        {/* Headline */}
        <Typography
          component="h1"
          sx={{
            fontSize:   "clamp(22px, 2.2vw, 32px)",
            fontWeight: 800,
            color:      "white",
            lineHeight: 1.28,
            mb:         2,
          }}
        >
          Find the right creator.{" "}
          <Box
            component="span"
            sx={{
              background:           "linear-gradient(90deg, #FF6B35 0%, #FF9A6C 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor:  "transparent",
              backgroundClip:       "text",
            }}
          >
            Run campaigns that convert.
          </Box>
        </Typography>

        <Typography sx={{ fontSize: "14px", color: "rgba(255,255,255,0.42)", mb: 5, lineHeight: 1.75, maxWidth: "380px" }}>
          Deep intelligence on India's creator economy — real audience quality,
          verified contacts, and campaign-ready analytics.
        </Typography>

        {/* Feature bullets */}
        <Stack spacing={2.5} sx={{ mb: 6 }}>
          {FEATURES.map((f, i) => (
            <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <CheckCircleOutlineIcon sx={{ color: "#FF6B35", fontSize: "17px", mt: "2px", flexShrink: 0 }} />
              <Typography sx={{ fontSize: "13.5px", color: "rgba(255,255,255,0.68)", lineHeight: 1.6 }}>
                {f}
              </Typography>
            </Box>
          ))}
        </Stack>

        {/* Stat pills */}
        <Box sx={{ display: "flex", gap: "32px", mb: "auto" }}>
          {STATS.map(({ v, l }) => (
            <Box key={l}>
              <Typography sx={{ fontSize: "22px", fontWeight: 800, color: "white", lineHeight: 1 }}>
                {v}
              </Typography>
              <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.32)", mt: 0.6, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                {l}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Bottom brand trust */}
        <Box sx={{ mt: 6 }}>
          <Box sx={{ height: "1px", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)", mb: 3 }} />
          <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.25)", textTransform: "uppercase", letterSpacing: "0.08em", mb: 2 }}>
            Trusted by
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px 20px" }}>
            {BRANDS.map((b) => (
              <Typography key={b} sx={{ fontSize: "12px", fontWeight: 600, color: "rgba(255,255,255,0.35)" }}>
                {b}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function AuthenticationPage() {
  const [cookies] = useCookies();
  const { type, code } = useParams();

  useEffect(() => {
    document.title = "Sign In | Meesha";
  }, []);

  if (
    cookies.user && cookies.user !== "undefined" &&
    cookies.auth && cookies.auth !== "undefined"
  ) {
    return <Navigate to="/filter/influencers" />;
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#060F24" }}>
      <LeftPanel />

      {/* Right panel */}
      <Box
        sx={{
          flex:           1,
          display:        "flex",
          flexDirection:  "column",
          alignItems:     "center",
          justifyContent: "center",
          overflowY:      "auto",
          px:             { xs: "24px", sm: "48px" },
          py:             "40px",
        }}
      >
        {/* Mobile-only logo */}
        <Box
          component="a"
          href="/"
          sx={{ display: { xs: "block", lg: "none" }, mb: 4 }}
        >
          <img src={Logo} alt="Meesha" width="110px" height="36px" />
        </Box>

        <Box sx={{ width: "100%", maxWidth: "400px" }}>
          {type === "signin" && <SigninSection />}
          {type === "signup" && <SignupSection refCode={code} />}
        </Box>
      </Box>
    </Box>
  );
}

export default AuthenticationPage;
